/**
 * ChatScrollContainer — scrollable container that auto-scrolls to the bottom
 * whenever `scrollKey` changes. Shared by 1:1 and room chat message lists.
 *
 * `hasTopFade` adds the thread page's scroll fade: a short band of the chat background
 * hugging the top edge, solid only at the very top, so messages dissolve under
 * the header instead of being cut mid-glyph. It shows only once the list has
 * been scrolled off its top, so a first message at rest is never washed out.
 */

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface ChatScrollContainerProps {
  children: React.ReactNode
  /** Change this value (e.g. messages.length) to trigger a scroll to bottom. */
  scrollKey: number | string
  className?: string
  /** Fade messages out under the header once scrolled (needs a flex-column parent). */
  hasTopFade?: boolean
  /**
   * Height in px of a header that floats over the top of this scroller. Messages scroll
   * under it and dissolve through a mask, as if the header were not there. The first
   * message rests just below it. Wins over `hasTopFade`.
   */
  topInset?: number
  'data-testid'?: string
}

export function ChatScrollContainer(props: ChatScrollContainerProps) {
  const { children, scrollKey, className, hasTopFade = false, topInset = 0, 'data-testid': dataTestId } = props

  const scrollRef = useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    // A freshly mounted turn keeps growing after this effect runs — cards
    // animate open and images/chips lay out, extending scrollHeight past what a
    // single synchronous measure sees. Measuring once here lands short of the
    // real bottom, leaving the newest turn below the fold. Re-pin to the bottom
    // each frame while the height is still changing (capped at ~30 frames) so
    // the whole newest turn is visible without the user scrolling down. Once it
    // settles, the loop ends and scrolling is the user's again.
    let raf = 0
    let frames = 0
    let lastHeight = -1
    const pin = () => {
      if (el.scrollHeight !== lastHeight) {
        lastHeight = el.scrollHeight
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
      }
      if (++frames < 30) {
        raf = requestAnimationFrame(pin)
      }
    }
    raf = requestAnimationFrame(pin)

    return () => cancelAnimationFrame(raf)
  }, [scrollKey])

  const onScroll = hasTopFade
    ? (event: React.UIEvent<HTMLDivElement>) => setIsScrolled(event.currentTarget.scrollTop > 0)
    : undefined

  const hasInset = topInset > 0
  const insetStyle = hasInset
    ? {
        // The classes already pad the content by 1rem; the header height comes on top.
        paddingTop: `calc(${topInset}px + 1rem)`,
        maskImage: `linear-gradient(to bottom, transparent 0, black ${topInset}px)`,
        WebkitMaskImage: `linear-gradient(to bottom, transparent 0, black ${topInset}px)`,
      }
    : undefined

  const scroller = (
    <div
      ref={scrollRef}
      className={cn('overflow-y-auto', className)}
      style={insetStyle}
      onScroll={onScroll}
      data-testid={dataTestId}
    >
      {children}
    </div>
  )

  if (!hasTopFade || hasInset) {
    return scroller
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-twin-chat-bg from-[15%] to-transparent transition-opacity duration-200',
          isScrolled ? 'opacity-100' : 'opacity-0',
        )}
        data-testid="chat-scroll-fade"
      />
      {scroller}
    </div>
  )
}
